import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { QuizQuestionFacade } from '@app/store/quiz-question';
import { QuizAnswerComponent } from '../quiz-answer/quiz-answer.component';

@Component({
    selector: 'app-quiz-question-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, UnavailableComponent, QuizAnswerComponent],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly quizQuestionFacade = inject(QuizQuestionFacade);
    private readonly fb = inject(NonNullableFormBuilder);

    private readonly id = this.route.parent?.snapshot.params['id'];
    readonly quizQuestion = this.quizQuestionFacade.quizQuestion(this.id);
    readonly loading = this.quizQuestionFacade.loading(this.id);
    readonly saving = this.quizQuestionFacade.saving;
    readonly error = this.quizQuestionFacade.error(this.id);

    readonly form = this.fb.group({
        questionText: this.fb.control('', [FormValidators.Required, FormValidators.Text]),
        answers: this.fb.array([this.createAnswerControl()]),
        correctAnswer: this.fb.control(0),
    });

    get answers() {
        return this.form.controls.answers;
    }

    get correctAnswer() {
        return this.form.controls.correctAnswer;
    }

    constructor() {
        if (this.id) {
            this.quizQuestionFacade.getQuizQuestion(this.id);
        }

        effect(() => {
            const quizQuestion = this.quizQuestion();
            if (quizQuestion) {
                this.form.controls.questionText.setValue(quizQuestion.questionText);
                this.answers.clear();
                let correctIndex = 0;
                for (let i = 0; i < quizQuestion.answers.length; i++) {
                    const control = this.createAnswerControl();
                    control.setValue(quizQuestion.answers[i].value);
                    this.answers.push(control);
                    if (quizQuestion.answers[i].correct) {
                        correctIndex = i;
                    }
                }
                this.correctAnswer.setValue(correctIndex);
                if (this.answers.length === 0) {
                    this.answers.push(this.createAnswerControl());
                }
            }
        });

        effect(() => {
            if (this.quizQuestionFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.quizQuestionFacade.saving() === false && this.quizQuestionFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.quizQuestionFacade.clear();
    }

    addAnswer(): void {
        this.answers.push(this.createAnswerControl());
    }

    removeAnswer(index: number): void {
        this.answers.removeAt(index);

        if (this.correctAnswer.value >= this.answers.length) {
            this.correctAnswer.setValue(this.answers.length - 1);
        }
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const quizQuestion = this.quizQuestion();
        if (this.form.invalid || !quizQuestion) return;

        this.form.disable();
        const { questionText, answers, correctAnswer } = this.form.getRawValue();

        this.quizQuestionFacade.updateQuizQuestion(quizQuestion.id, {
            questionText,
            answers: answers.map((answer, index) => ({
                value: answer,
                correct: index === correctAnswer,
            })),
        });
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }

    private createAnswerControl() {
        return this.fb.control('', [FormValidators.Required, FormValidators.Text]);
    }
}
